import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import HeroImage from './HeroImage';
import { HeroData } from '../models/HeroData';
import DraftState from '../models/DraftState';
import { useDraft } from '../contexts/DraftContext';
import { useNavigate } from 'react-router-dom';
import ConfirmationDialog from './ConfirmationDialog';

const DraftBoardContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #0b0d14;
  background-image: linear-gradient(to bottom, #161926, #0b0d14);
  padding: 12px;
  border-radius: 12px;
  width: 280px;
  min-width: 280px;
  max-width: 280px;
  flex-shrink: 0;
  margin: 0;
  position: relative;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
  
  @media (max-width: 768px) {
    width: 260px;
    min-width: 260px;
    max-width: 260px;
    padding: 8px;
  }
  
  @media (max-width: 480px) {
    width: 240px;
    min-width: 240px;
    max-width: 240px;
    padding: 6px;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #333;
`;

const TeamsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 10px;
  align-items: center;
`;

const TeamHeader = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: ${props => props.$team === 'Radiant' ? '#92ff92' : '#ff6b6b'};
  text-align: center;
  width: 50%;
  text-shadow: 0 0 8px ${props => props.$team === 'Radiant' ? 'rgba(146, 255, 146, 0.5)' : 'rgba(255, 107, 107, 0.5)'};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 0;
`;

const ClearButton = styled.button`
  background-color: #d32f2f;
  background-image: linear-gradient(to bottom, #d32f2f, #b71c1c);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  
  &:hover {
    background-image: linear-gradient(to bottom, #e33c3c, #c82121);
    transform: translateY(-1px);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.4);
  }
  
  &:active {
    transform: translateY(1px);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
  }
`;

const PicksContainer = styled.div`
  display: flex;
  justify-content: center;
  position: relative;
  gap: 16px;
  background: linear-gradient(to bottom, rgba(20, 22, 34, 0.8), rgba(14, 16, 26, 0.8));
  padding: 12px 0;
  border-radius: 8px;
`;

const TeamColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${props => props.$team === 'Radiant' ? 'flex-end' : 'flex-start'};
  gap: 12px;
`;

// Box sizes based on their importance in the draft
const getBoxSize = (position) => {
  // Use the same large box sequences from the DraftState model
  if (DraftState.getLargeBoxSequences().includes(position.toString())) {
    return '80px';  // Height for large boxes - reduced from 90px
  }
  return '50px';  // Height for standard boxes - reduced from 60px
};

const PickBox = styled.div.attrs(props => ({
  'data-position': props.position,
  'data-team': props.team
}))`
  border: 1px solid #444;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${props => getBoxSize(props.$position)};
  width: ${props => {
    // Width determination based on position, using DraftState
    if (DraftState.getLargeBoxSequences().includes(props.$position.toString())) {
      return '120px';  // Width for large boxes - reduced from 135px
    }
    return '80px';  // Width for standard boxes - reduced from 90px
  }};
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: #111215;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  overflow: hidden; /* Ensure images don't overflow */
  padding: 0; /* Remove padding to allow images to fill the box */
  
  &:hover {
    border-color: ${props => props.$team === 'Radiant' ? '#92ff92' : '#ff6b6b'};
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.4);
  }
  
  &.selected {
    border-color: ${props => props.$team === 'Radiant' ? '#92ff92' : '#ff6b6b'};
    border-width: 2px;
    box-shadow: 0 0 10px ${props => props.$team === 'Radiant' ? 'rgba(146, 255, 146, 0.5)' : 'rgba(255, 107, 107, 0.5)'};
  }
  
  &.filled {
    background-color: ${props => props.$team === 'Radiant' ? 'rgba(146, 255, 146, 0.1)' : 'rgba(255, 107, 107, 0.1)'};
  }
`;

const PositionLabel = styled.div`
  position: absolute;
  top: 4px;
  left: 4px;
  font-size: 12px;
  font-weight: bold;
  color: rgba(255, 255, 255, 0.8);
  background-color: rgba(0, 0, 0, 0.5);
  padding: 2px 5px;
  border-radius: 4px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
  z-index: 2; /* Ensure it's above the hero image */
`;

const HeroContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 0; /* Ensure it's below any overlays like the ban X */
  
  /* Ensure HeroImage fills the container */
  > div {
    width: 100%;
    height: 100%;
  }
`;

const BanIndicator = styled.div`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: rgba(211, 47, 47, 0.8);
  color: white;
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2; /* Ensure it's above the hero image */
`;

const BanCross = styled.div`
  position: absolute;
  bottom: 4px;
  left: 4px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ff6b6b;
  font-weight: bold;
  font-size: 18px;
  pointer-events: none;
  text-shadow: 0 0 4px rgba(0, 0, 0, 0.9);
  z-index: 2;
  
  &::before {
    content: '';
    position: absolute;
    width: 16px;
    height: 16px;
    background-color: rgba(0, 0, 0, 0.3);
    border-radius: 50%;
    z-index: -1;
  }
`;

const HeroSelectPopup = styled.div`
  position: fixed;
  z-index: 1000;
  width: 90vw;
  max-width: 500px;
  max-height: 80vh;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  background-color: rgba(14, 16, 22, 0.97);
  border: 1px solid #444;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.9);
  display: flex;
  flex-direction: column;
  animation: popupFadeIn 0.25s cubic-bezier(0.23, 1, 0.32, 1);
  
  @keyframes popupFadeIn {
    from {
      opacity: 0;
      transform: translate(-50%, -50%) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }
  }
`;

const SearchInput = styled.input`
  padding: 14px 16px;
  background-color: rgba(10, 12, 16, 0.7);
  color: white;
  border: none;
  border-bottom: 1px solid #555;
  margin-bottom: 0;
  font-size: 15px;
  width: 100%;
  box-sizing: border-box;
  position: sticky;
  top: 0;
  z-index: 3;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    background-color: rgba(15, 18, 24, 0.9);
    border-bottom-color: #4CAF50;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.5);
  font-size: 16px;
  pointer-events: none;
`;

const SelectBoxPlaceholder = styled.div.attrs(props => ({
  shouldShow: props.show
}))`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  color: #aaa;
  font-size: 14px;
  line-height: 1.5;
  max-width: 300px;
  padding: 24px;
  background-color: rgba(0, 0, 0, 0.4);
  border-radius: 8px;
  pointer-events: none;
  
  .instruction {
    font-weight: bold;
    color: white;
    margin-bottom: 10px;
  }
  
  .arrow {
    font-size: 24px;
    margin: 5px 0;
  }
  
  display: ${props => props.shouldShow ? 'block' : 'none'};
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 100px));
  gap: 10px;
  padding: 16px;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 400px;
  flex: 1;
  justify-content: center;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: #555;
    border-radius: 4px;
    
    &:hover {
      background-color: #666;
    }
  }
  
  &::-webkit-scrollbar-track {
    background-color: rgba(0, 0, 0, 0.3);
    border-radius: 4px;
  }
`;

const HeroOption = styled.div`
  cursor: pointer;
  padding: 6px 4px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: all 0.2s;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  }
  
  &:active {
    transform: translateY(1px);
  }
  
  .hero-name {
    font-size: 11px;
    margin-top: 6px;
    text-align: center;
    color: #fff;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
    font-weight: 500;
  }
`;

const PopupControls = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px;
  border-top: 1px solid #444;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(5px);
`;

const RemoveButton = styled.button`
  background-color: #333;
  background-image: linear-gradient(to bottom, #444, #333);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px 14px;
  cursor: pointer;
  font-size: 12px;
  font-weight: bold;
  transition: all 0.2s;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
  
  &:hover {
    background-image: linear-gradient(to bottom, #555, #444);
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.5);
  }
  
  &:active {
    transform: translateY(1px);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
    background-image: linear-gradient(to bottom, #333, #2a2a2a);
  }
`;

const BanBox = styled(PickBox)`
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.25);
    pointer-events: none;
    border-radius: 7px;
    z-index: 1; /* Ensure the overlay sits above the image */
  }
  
  &.filled::after {
    background-color: rgba(0, 0, 0, 0.35);
  }
`;

const LegendContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
  padding: 8px 12px;
  background-color: rgba(0, 0, 0, 0.4);
  border-radius: 6px;
  border: 1px solid #333;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #ddd;
`;

const LegendBanIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.3);
  color: #ff6b6b;
  font-weight: bold;
  font-size: 14px;
`;

const AddScenarioButton = styled.button`
  background-color: #2196F3;
  background-image: linear-gradient(to bottom, #2196F3, #1976D2);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 120px;
  height: 36px;
  font-size: 14px;
  
  &:hover {
    background-image: linear-gradient(to bottom, #42A5F5, #1E88E5);
    transform: translateY(-1px);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.4);
  }
  
  &:active {
    transform: translateY(1px);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
  }
  
  @media (max-width: 768px) {
    padding: 6px 10px;
    min-width: 100px;
    height: 32px;
    font-size: 12px;
  }
  
  svg {
    margin-right: 6px;
  }
`;

const NewDraftButton = styled.button`
  background-color: #1976D2;
  background-image: linear-gradient(to bottom, #1976D2, #1565C0);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 120px;
  height: 36px;
  font-size: 14px;
  
  &:hover {
    background-image: linear-gradient(to bottom, #1E88E5, #1976D2);
    transform: translateY(-1px);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.4);
  }
  
  &:active {
    transform: translateY(1px);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
  }
  
  @media (max-width: 768px) {
    padding: 6px 10px;
    min-width: 100px;
    height: 32px;
    font-size: 12px;
  }
  
  svg {
    margin-right: 6px;
  }
`;

// Create a new ScenarioContainer for horizontal scrolling
const ScenarioContainer = styled.div`
  display: flex;
  overflow-x: auto;
  gap: 20px;
  padding: 10px 0;
  width: 100%;
  min-width: 100vw;
  scrollbar-width: thin;
  scrollbar-color: #444 #222;
  justify-content: center;
  
  &::-webkit-scrollbar {
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #222;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: #444;
    border-radius: 4px;
    
    &:hover {
      background-color: #555;
    }
  }
`;

// Add a DeleteScenarioButton component
const DeleteScenarioButton = styled.button`
  background-color: transparent;
  color: #ff4d4d;
  border: 1px solid #ff4d4d;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 11px;
  font-weight: bold;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  min-width: 60px;
  justify-content: center;
  
  &:hover {
    background-color: rgba(255, 77, 77, 0.1);
  }
  
  svg {
    margin-right: 4px;
  }
`;

// Add a DuplicateScenarioButton component
const DuplicateScenarioButton = styled.button`
  background-color: transparent;
  color: #61dafb;
  border: 1px solid #61dafb;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 11px;
  font-weight: bold;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  min-width: 70px;
  justify-content: center;
  
  &:hover {
    background-color: rgba(97, 218, 251, 0.1);
  }
  
  svg {
    margin-right: 4px;
  }
`;

const ScenarioTitle = styled.div`
  font-size: 16px;
  font-weight: bold;
  text-align: center;
  margin-bottom: 10px;
  color: #eee;
`;

const ScenarioInput = styled.input`
  background-color: rgba(0, 0, 0, 0.3);
  border: 1px solid #444;
  border-radius: 4px;
  color: white;
  padding: 8px 10px;
  width: 100%;
  margin-bottom: 12px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #61dafb;
    box-shadow: 0 0 0 2px rgba(97, 218, 251, 0.2);
  }
  
  &::placeholder {
    color: #888;
  }
`;

const TopButtonRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  width: 100%;
  margin-bottom: 16px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 4px;
    margin-bottom: 12px;
  }
`;

const ScenarioBoardsRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 16px 8px;
  box-sizing: border-box;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  
  /* Custom scrollbar for webkit browsers */
  &::-webkit-scrollbar {
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.5);
  }
  
  @media (max-width: 768px) {
    gap: 12px;
    padding: 12px 4px;
  }
`;

const InfoInputRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  width: 100%;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 4px;
    margin-bottom: 8px;
  }

  @media (max-width: 480px) {
    display: none;
  }
`;

const InfoInput = styled.input`
  padding: 8px 10px;
  border-radius: 4px;
  border: 1px solid #444;
  background: #181a20;
  color: #fff;
  font-size: 14px;
  min-width: 120px;
  max-width: 180px;
  outline: none;
  transition: border 0.2s;
  box-sizing: border-box;

  &:focus {
    border-color: #61dafb;
  }

  @media (max-width: 768px) {
    padding: 5px 6px;
    font-size: 12px;
    min-width: 90px;
    max-width: 120px;
  }

  @media (max-width: 480px) {
    padding: 3px 4px;
    font-size: 10px;
    min-width: 60px;
    max-width: 110px;
    flex: 1 1 auto;
  }
`;

const InfoDateInput = styled(InfoInput).attrs({ type: 'date' })`
  min-width: 120px;

  @media (max-width: 768px) {
    min-width: 90px;
  }

  @media (max-width: 480px) {
    min-width: 60px;
  }
`;

const ToggleSwitchContainer = styled.div`
  display: flex;
  align-items: center;
  margin-right: 24px;

  @media (max-width: 768px) {
    margin-right: 0;
  }
`;

const ToggleSwitch = styled.div`
  display: flex;
  border: 2px solid #1de9b6;
  border-radius: 999px;
  overflow: hidden;
  background: #181a20;
  min-width: 160px;
  height: 42px;

  @media (max-width: 768px) {
    min-width: 140px;
    height: 36px;
  }

  @media (max-width: 480px) {
    min-width: 120px;
    height: 32px;
  }
`;

const ToggleOption = styled.button`
  background: ${props => props.$active ? '#1de9b6' : 'transparent'};
  color: ${props => props.$active ? '#000' : '#aaa'};
  border: none;
  padding: 0 20px;
  cursor: pointer;
  font-size: 14px;
  font-weight: ${props => props.$active ? 'bold' : 'normal'};
  transition: all 0.2s ease;
  flex: 1;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;

  &:hover {
    background: ${props => props.$active ? '#26efc2' : 'rgba(29, 233, 182, 0.1)'};
    color: ${props => props.$active ? '#000' : '#1de9b6'};
  }

  &:first-child {
    border-top-left-radius: 999px;
    border-bottom-left-radius: 999px;
  }

  &:last-child {
    border-top-right-radius: 999px;
    border-bottom-right-radius: 999px;
  }

  @media (max-width: 768px) {
    padding: 0 12px;
    font-size: 13px;
  }

  @media (max-width: 480px) {
    padding: 0 8px;
    font-size: 11px;
  }
`;

// Add drag-and-drop overlay styles
const DragOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(33, 150, 243, 0.08);
  backdrop-filter: blur(2px);
  display: ${props => props.$show ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 9999;
  border: 2px dashed rgba(33, 150, 243, 0.4);
`;

const DragOverlayContent = styled.div`
  background-color: rgba(0, 0, 0, 0.7);
  padding: 24px 32px;
  border-radius: 8px;
  text-align: center;
  color: #2196f3;
  font-size: 18px;
  font-weight: 500;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
`;

const DragOverlayIcon = styled.div`
  font-size: 40px;
  margin-bottom: 8px;
  opacity: 0.8;
`;

const MainContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  padding: 8px;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 4px;
  }
`;

/**
 * DraftBoard Component
 * 
 * Displays the draft board with pick boxes for Radiant and Dire
 */
const DraftBoard = () => {
  const {
    scenariosFirstPick,
    scenariosSecondPick,
    addScenario,
    duplicateScenario,
    deleteScenario,
    updateScenario,
    saveDraft,
    newDraft,
    teamName, setTeamName,
    tournament, setTournament,
    stage, setStage,
    draftDate, setDraftDate,
    hasUnsavedChanges,
    currentDraftId,
    loadDraft,
    importDraftData
  } = useDraft();

  const navigate = useNavigate();

  // Toggle state: 'first' or 'second', default to 'first' instead of 'second'
  const [pickMode, setPickMode] = useState('first');

  // Use the correct scenarios for the current pick mode
  const scenarios = pickMode === 'first' ? scenariosFirstPick : scenariosSecondPick;

  // UI state
  const [selectedBox, setSelectedBox] = useState(-1);
  const [heroSearch, setHeroSearch] = useState('');
  const [showPopup, setShowPopup] = useState(false);
  const [activeScenarioId, setActiveScenarioId] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const popupRef = useRef(null);
  const boxRefs = useRef({});
  const scenarioRefs = useRef({});
  const scrollContainerRef = useRef(null);

  // Hero filtering logic with improved search
  const allHeroes = Object.values(HeroData);
  const filteredHeroes = heroSearch
    ? allHeroes.filter(hero => {
        const searchTerm = heroSearch.toLowerCase().trim();
        const heroName = hero.name.toLowerCase();
        
        // Direct name match
        if (heroName.includes(searchTerm)) {
          return true;
        }
        
        // Split hero name into words (handle spaces, hyphens, and apostrophes)
        const words = heroName.split(/[\s\-']+/).filter(word => word.length > 0);
        
        // Create abbreviation from first letters
        const abbreviation = words.map(word => word.charAt(0)).join('');
        
        // Check exact abbreviation match (e.g., "aa" -> "Ancient Apparition")
        if (abbreviation === searchTerm) {
          return true;
        }
        
        // Check partial abbreviation match (e.g., "a" matches "Ancient Apparition")
        if (abbreviation.startsWith(searchTerm)) {
          return true;
        }
        
        // Check if search term matches any word start
        if (words.some(word => word.startsWith(searchTerm))) {
          return true;
        }
        
        // Special case: if search is very short, be more restrictive
        if (searchTerm.length === 1) {
          return abbreviation.startsWith(searchTerm) || words.some(word => word.startsWith(searchTerm));
        }
        
        return false;
      }).sort((a, b) => {
        // Sort results by relevance
        const searchTerm = heroSearch.toLowerCase().trim();
        const aName = a.name.toLowerCase();
        const bName = b.name.toLowerCase();
        
        // Exact name starts get priority
        const aStartsWith = aName.startsWith(searchTerm);
        const bStartsWith = bName.startsWith(searchTerm);
        if (aStartsWith && !bStartsWith) return -1;
        if (bStartsWith && !aStartsWith) return 1;
        
        // Then abbreviation matches
        const aWords = aName.split(/[\s\-']+/).filter(word => word.length > 0);
        const bWords = bName.split(/[\s\-']+/).filter(word => word.length > 0);
        const aAbbr = aWords.map(word => word.charAt(0)).join('');
        const bAbbr = bWords.map(word => word.charAt(0)).join('');
        
        const aAbbrMatch = aAbbr === searchTerm;
        const bAbbrMatch = bAbbr === searchTerm;
        if (aAbbrMatch && !bAbbrMatch) return -1;
        if (bAbbrMatch && !aAbbrMatch) return 1;
        
        // Finally alphabetical
        return aName.localeCompare(bName);
      })
    : allHeroes;

  // Set active scenario to the first one if not set, and on pickMode change
  useEffect(() => {
    if (scenarios.length > 0 && !activeScenarioId) {
      setActiveScenarioId(scenarios[0].id);
    }
  }, [scenarios, activeScenarioId, pickMode]);

  // Center the first scenario only on pickMode change (not when scenarios are added/duplicated)
  useEffect(() => {
    const centerFirstScenario = () => {
      if (scenarios.length > 0 && scrollContainerRef.current && scenarioRefs.current[scenarios[0].id]) {
        // Use a small delay to ensure the layout is rendered
        setTimeout(() => {
          const firstScenario = scenarioRefs.current[scenarios[0].id];
          const container = scrollContainerRef.current;
          
          if (firstScenario && container) {
            const containerRect = container.getBoundingClientRect();
            const scenarioRect = firstScenario.getBoundingClientRect();
            
            // Calculate the scroll position to center the first scenario
            const scrollLeft = firstScenario.offsetLeft - (container.clientWidth / 2) + (scenarioRect.width / 2);
            
            container.scrollTo({
              left: scrollLeft,
              behavior: 'smooth'
            });
          }
        }, 100);
      }
    };

    centerFirstScenario();
  }, [pickMode]); // Only center when pickMode changes, not when scenarios are added

  // Also center on initial mount
  useEffect(() => {
    const initialCenter = () => {
      if (scenarios.length > 0 && scrollContainerRef.current && scenarioRefs.current[scenarios[0].id]) {
        setTimeout(() => {
          const firstScenario = scenarioRefs.current[scenarios[0].id];
          const container = scrollContainerRef.current;
          
          if (firstScenario && container) {
            const containerRect = container.getBoundingClientRect();
            const scenarioRect = firstScenario.getBoundingClientRect();
            
            // Calculate the scroll position to center the first scenario
            const scrollLeft = firstScenario.offsetLeft - (container.clientWidth / 2) + (scenarioRect.width / 2);
            
            container.scrollTo({
              left: scrollLeft,
              behavior: 'auto' // No animation for initial positioning
            });
          }
        }, 300); // Longer delay for initial mount to ensure layout is complete
      }
    };

    initialCenter();
  }, []); // Empty dependency array for mount only

  // Get the active scenario
  const activeScenario = scenarios.find(s => s.id === activeScenarioId) || scenarios[0];

  // Add scenario handler
  const handleAddScenario = React.useCallback(() => {
    addScenario(pickMode);
  }, [addScenario, pickMode]);

  // Update a scenario's picks in the current pick mode
  const updateScenarioPicks = (scenarioId, newPicks) => updateScenario(pickMode, scenarioId, { picks: newPicks });

  // Handle box click to show hero selection popup
  const handleBoxClick = (scenarioId, position) => {
    setActiveScenarioId(scenarioId);
    setSelectedBox(position);
    setShowPopup(true);
    setHeroSearch('');
  };

  // Handle hero selection from popup
  const handleHeroSelect = (hero) => {
    if (selectedBox === -1 || !activeScenario) return;
    
    // Create a complete hero object with all required properties
    const completeHero = {
      id: hero.id,
      name: hero.name,
      imageUrl: hero.imageUrl || `https://cdn.cloudflare.steamstatic.com/apps/dota2/images/dota_react/heroes/${hero.id || hero.name.toLowerCase().replace(/\s/g, '_')}.png`,
      attributes: hero.attributes || {}
    };
    
    // Create a copy of the current picks array
    const newPicks = activeScenario.picks ? 
      [...activeScenario.picks] : 
      Array(24).fill(null);

    // Update only the selected position
    newPicks[selectedBox - 1] = { 
      hero: completeHero, 
      isBanned: false 
    };

    // Update the scenario with the new picks
    updateScenarioPicks(activeScenario.id, newPicks);
    setShowPopup(false);
  };

  // Handle toggling a hero as banned
  const toggleBan = () => {
    if (selectedBox === -1 || !activeScenario || !activeScenario.picks[selectedBox - 1]) return;
    const newPicks = [...activeScenario.picks];
    newPicks[selectedBox - 1] = {
      ...newPicks[selectedBox - 1],
      isBanned: !newPicks[selectedBox - 1].isBanned
    };
    updateScenarioPicks(activeScenario.id, newPicks);
  };

  // Handle removing a hero
  const removeHero = () => {
    if (selectedBox === -1 || !activeScenario) return;
    const newPicks = [...activeScenario.picks];
    newPicks[selectedBox - 1] = null;
    updateScenarioPicks(activeScenario.id, newPicks);
    setShowPopup(false);
  };

  // Handle keyboard events in search input
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter' && filteredHeroes.length > 0) {
      handleHeroSelect(filteredHeroes[0]);
    } else if (e.key === 'Escape') {
      setShowPopup(false);
    }
  };

  // Clear all picks for a scenario
  const clearScenario = (scenarioId) => {
    updateScenario(pickMode, scenarioId, { picks: Array(24).fill(null) });
  };

  // Save draft to localStorage (include info fields)
  const handleSaveDraft = () => {
    saveDraft({
      teamName,
      tournament,
      stage,
      date: draftDate,
      title: teamName ? `Draft for ${teamName}` : `Draft ${new Date().toLocaleDateString()}`,
      savedAt: new Date().toISOString(),
    });
  };

  // Handle new draft button
  const handleNewDraft = () => {
    const result = newDraft();
    if (result && result.needsConfirmation) {
      setPendingAction({ type: 'newDraft' });
      setShowConfirmation(true);
    }
  };

  // Handle confirmation dialog actions
  const handleConfirmSave = async () => {
    try {
      await handleSaveDraft();
      await executePendingAction(true); // Execute with skipConfirmation = true
    } catch (error) {
      console.error('Error saving draft:', error);
    } finally {
      setShowConfirmation(false);
      setPendingAction(null);
    }
  };

  const handleConfirmDiscard = async () => {
    try {
      await executePendingAction(true); // Execute with skipConfirmation = true
    } catch (error) {
      console.error('Error executing action:', error);
    } finally {
      setShowConfirmation(false);
      setPendingAction(null);
    }
  };

  const handleConfirmCancel = () => {
    setShowConfirmation(false);
    setPendingAction(null);
  };

  // Execute the pending action
  const executePendingAction = async (skipConfirmation = false) => {
    if (!pendingAction) return;

    try {
      switch (pendingAction.type) {
        case 'import':
          const importResult = await importDraftData(pendingAction.data, skipConfirmation);
          if (importResult && importResult.id) {
            // Handle successful import (tracking, caching, navigation)
            const tracking = JSON.parse(localStorage.getItem('dota2_draft_tracking') || '{}');
            const draftData = pendingAction.data;
            const file = pendingAction.file;

            const cleanString = (str) => {
              return str ? str.replace(/[^a-z0-9]/gi, '_').replace(/_+/g, '_').replace(/^_|_$/g, '') : '';
            };

            const teamName = cleanString(draftData.teamName) || 'Team';
            const tournamentName = cleanString(draftData.tournament) || 'Tournament';
            const stageName = cleanString(draftData.stage) || 'Stage';
            const dateString = draftData.date || new Date().toISOString().slice(0, 10);

            const displayTitle = `${teamName}_${tournamentName}_${stageName}_${dateString}`;

            tracking[draftData.id] = {
              id: draftData.id,
              filename: file.name,
              title: displayTitle,
              tournament: draftData.tournament || '',
              stage: draftData.stage || '',
              date: draftData.date || draftData.createdAt,
              updatedAt: draftData.updatedAt,
              createdAt: draftData.createdAt,
              scenariosFirstPick: draftData.scenariosFirstPick ? true : false,
              scenariosSecondPick: draftData.scenariosSecondPick ? true : false
            };

            localStorage.setItem('dota2_draft_tracking', JSON.stringify(tracking));

            try {
              localStorage.setItem(`draft_cache_${draftData.id}`, JSON.stringify(draftData));
              console.log('Draft imported successfully:', draftData.id);
            } catch (cacheError) {
              console.warn('Could not cache draft data', cacheError);
            }

            if (currentDraftId !== draftData.id) {
              navigate(`/draft/${draftData.id}`);
            }
          }
          break;
        case 'newDraft':
          newDraft(skipConfirmation);
          break;
        default:
          console.warn('Unknown pending action type:', pendingAction.type);
      }
    } catch (error) {
      console.error('Error executing pending action:', error);
      throw error;
    }
  };

  // Drag and drop functionality
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only hide overlay if we're leaving the main container
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    const file = files[0];

    // Check if it's a JSON file
    if (!file.name.toLowerCase().endsWith('.json')) {
      console.warn('Invalid file type. Please drop a JSON file.');
      return;
    }

    try {
      // Read the file content
      const text = await file.text();
      let draftData;

      try {
        draftData = JSON.parse(text);
      } catch (parseError) {
        console.warn('Invalid JSON file format');
        return;
      }

      // Validate that it's a draft file
      if (!draftData || (!draftData.id && !draftData.scenariosFirstPick && !draftData.scenariosSecondPick)) {
        console.warn('This doesn\'t appear to be a valid draft file');
        return;
      }

      // Generate an ID if none exists
      if (!draftData.id) {
        draftData.id = `draft_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      }

      // Add missing fields if necessary
      draftData.createdAt = draftData.createdAt || draftData.date || new Date().toISOString();
      draftData.updatedAt = draftData.updatedAt || draftData.createdAt;

      // Use the importDraftData function to handle the import
      const importResult = await importDraftData(draftData);

      // Check if confirmation is needed
      if (importResult && importResult.needsConfirmation) {
        setPendingAction({
          type: 'import',
          data: importResult.draftData,
          file: file
        });
        setShowConfirmation(true);
        return;
      }

      if (importResult && importResult.id) {
        // Track the saved draft for future reference
        const tracking = JSON.parse(localStorage.getItem('dota2_draft_tracking') || '{}');

        // Generate display title that matches filename format
        const cleanString = (str) => {
          return str ? str.replace(/[^a-z0-9]/gi, '_').replace(/_+/g, '_').replace(/^_|_$/g, '') : '';
        };

        const teamName = cleanString(draftData.teamName) || 'Team';
        const tournamentName = cleanString(draftData.tournament) || 'Tournament';
        const stageName = cleanString(draftData.stage) || 'Stage';
        const dateString = draftData.date || new Date().toISOString().slice(0, 10);

        const displayTitle = `${teamName}_${tournamentName}_${stageName}_${dateString}`;

        tracking[draftData.id] = {
          id: draftData.id,
          filename: file.name,
          title: displayTitle,
          tournament: draftData.tournament || '',
          stage: draftData.stage || '',
          date: draftData.date || draftData.createdAt,
          updatedAt: draftData.updatedAt,
          createdAt: draftData.createdAt,
          scenariosFirstPick: draftData.scenariosFirstPick ? true : false,
          scenariosSecondPick: draftData.scenariosSecondPick ? true : false
        };

        localStorage.setItem('dota2_draft_tracking', JSON.stringify(tracking));

        // Cache the draft data for future quick loading
        try {
          localStorage.setItem(`draft_cache_${draftData.id}`, JSON.stringify(draftData));
          console.log('Draft imported successfully:', draftData.id);
        } catch (cacheError) {
          console.warn('Could not cache draft data', cacheError);
        }

        // Navigate to the imported draft if we're not already viewing it
        if (currentDraftId !== draftData.id) {
          navigate(`/draft/${draftData.id}`);
        }
      }

    } catch (err) {
      console.error('Error importing dropped file:', err);
    }
  };

  // Prevent default drag behavior on the entire page
  useEffect(() => {
    const preventDefault = (e) => {
      e.preventDefault();
    };

    document.addEventListener('dragover', preventDefault);
    document.addEventListener('drop', preventDefault);

    return () => {
      document.removeEventListener('dragover', preventDefault);
      document.removeEventListener('drop', preventDefault);
    };
  }, []);

  // Define which position numbers belong to Radiant and Dire
  const radiantPositions = [1, 4, 7, 8, 10, 11, 14, 15, 18, 19, 22, 23];
  
  // Define which positions are ban boxes
  const banPositions = [1, 2, 3, 4, 5, 6, 7, 10, 11, 12, 19, 20, 21, 22];

  // Create pick box for a given position and scenario
  const renderPickBox = React.useCallback((scenarioId, position) => {
    const scenario = scenarios.find(s => s.id === scenarioId);
    if (!scenario) return null;
    
    const isRadiant = radiantPositions.includes(position);
    const team = isRadiant ? 'Radiant' : 'Dire';
    const pick = scenario.picks ? scenario.picks[position - 1] : null;
    const isFilled = Boolean(pick?.hero);
    const isBanBox = banPositions.includes(position);
    const isSelected = activeScenarioId === scenarioId && selectedBox === position;
    const isActive = isSelected && showPopup;
    
    const BoxComponent = isBanBox ? BanBox : PickBox;
    
    // Determine the appropriate size based on the position
    const isLargeBox = DraftState.getLargeBoxSequences().includes(position.toString());
    const imageSize = isLargeBox ? 'large' : 'medium';
    
    return (
      <BoxComponent 
        key={`${scenarioId}_${position}`}
        $position={position}
        $team={team}
        data-position={position}
        data-team={team}
        className={`${isSelected ? 'selected' : ''} ${isFilled ? 'filled' : ''} ${isActive ? 'active' : ''}`}
        onClick={() => handleBoxClick(scenarioId, position)}
        ref={el => boxRefs.current[`${scenarioId}_${position}`] = el}
        style={{
          ...(isActive && {
            boxShadow: `0 0 12px 2px ${isRadiant ? 'rgba(146, 255, 146, 0.7)' : 'rgba(255, 107, 107, 0.7)'}`
          })
        }}
      >
        <PositionLabel>{position}</PositionLabel>
        <HeroContainer>
          {pick?.hero && (
            <HeroImage 
              key={`${pick.hero.id}_${position}_${scenarioId}`}
              hero={pick.hero} 
              size={imageSize}
              $grayscale={isBanBox}
            />
          )}
        </HeroContainer>
        {pick?.isBanned && <BanIndicator>X</BanIndicator>}
        {isBanBox && <BanCross>✕</BanCross>}
      </BoxComponent>
    );
  }, [scenarios, activeScenarioId, selectedBox, showPopup, handleBoxClick, boxRefs]);

  return (
    <MainContainer 
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <InfoInputRow>
        <InfoInput
          placeholder="Team Name"
          value={teamName}
          onChange={e => setTeamName(e.target.value)}
        />
        <InfoInput
          placeholder="Tournament Name"
          value={tournament}
          onChange={(e) => setTournament(e.target.value)}
        />
        <InfoInput
          type="text"
          value={stage}
          onChange={(e) => setStage(e.target.value)}
          placeholder="Stage Name"
        />
        <InfoDateInput
          value={draftDate}
          onChange={e => setDraftDate(e.target.value)}
        />
      </InfoInputRow>
      <TopButtonRow>
        <ToggleSwitchContainer>
          <ToggleSwitch>
            <ToggleOption
              $active={pickMode === 'first'}
              onClick={() => setPickMode('first')}
            >
              1st Pick
            </ToggleOption>
            <ToggleOption
              $active={pickMode === 'second'}
              onClick={() => setPickMode('second')}
            >
              2nd Pick
            </ToggleOption>
          </ToggleSwitch>
        </ToggleSwitchContainer>
        <AddScenarioButton onClick={handleAddScenario}>
          <span style={{ fontSize: '18px', marginRight: '5px' }}>+</span> Add Scenario
        </AddScenarioButton>
        <NewDraftButton onClick={handleNewDraft}>
          <span style={{ fontSize: '18px', marginRight: '5px' }}>📄</span> New Draft
        </NewDraftButton>
        <Button
          onClick={handleSaveDraft}
          style={{
            backgroundColor: '#4CAF50',
            backgroundImage: 'linear-gradient(to bottom, #4CAF50, #388E3C)',
            color: 'white',
            padding: '10px 16px',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            minWidth: '140px',
            height: '42px',
            fontWeight: 'bold',
            transition: 'all 0.2s',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundImage = 'linear-gradient(to bottom, #66BB6A, #4CAF50)';
            e.target.style.transform = 'translateY(-1px)';
            e.target.style.boxShadow = '0 3px 6px rgba(0, 0, 0, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundImage = 'linear-gradient(to bottom, #4CAF50, #388E3C)';
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.3)';
          }}
          onMouseDown={(e) => {
            e.target.style.transform = 'translateY(1px)';
            e.target.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.4)';
          }}
          onMouseUp={(e) => {
            e.target.style.transform = 'translateY(-1px)';
            e.target.style.boxShadow = '0 3px 6px rgba(0, 0, 0, 0.4)';
          }}
        >
          Save Draft
        </Button>
      </TopButtonRow>
      <ScenarioBoardsRow ref={scrollContainerRef}>
        {scenarios.map((scenario) => (
    <DraftBoardContainer 
            key={scenario.id}
      className="draft-board"
            ref={el => scenarioRefs.current[scenario.id] = el}
            style={{ minWidth: 280, maxWidth: 300 }}
    >
      {/* Scenario title input */}
      <ScenarioInput 
        type="text" 
        placeholder="Enter scenario name..."
              value={scenario.title || ''}
              onChange={(e) => updateScenario(pickMode, scenario.id, { title: e.target.value })}
      />
      
      {/* Scenario number and actions */}
            <ScenarioTitle>
              {pickMode === 'first' ? 
                <span style={{ color: '#1de9b6' }}>1st Pick</span> : 
                <span style={{ color: '#61dafb' }}>2nd Pick</span>} Scenario {scenarios.indexOf(scenario) + 1}
            </ScenarioTitle>
      
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px', gap: '8px' }}>
              <DuplicateScenarioButton onClick={() => duplicateScenario(pickMode, scenario.id)}>
          <span style={{ marginRight: '4px' }}>📋</span> Duplicate
        </DuplicateScenarioButton>
              {scenarios.length > 1 && (
                <DeleteScenarioButton onClick={() => deleteScenario(pickMode, scenario.id)}>
            <span style={{ marginRight: '4px' }}>🗑</span> Delete
          </DeleteScenarioButton>
        )}
              <ClearButton onClick={() => updateScenario(pickMode, scenario.id, { picks: Array(24).fill(null) })} style={{ padding: '4px 8px', fontSize: '11px', minWidth: '65px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          Clear All
        </ClearButton>
      </div>
      
      <TeamsContainer>
        <TeamHeader $team="Radiant">Radiant</TeamHeader>
        <TeamHeader $team="Dire">Dire</TeamHeader>
      </TeamsContainer>
      
      <PicksContainer>
        <TeamColumn $team="Radiant">
          {/* Radiant column */}
                {renderPickBox(scenario.id, 1)}
                {renderPickBox(scenario.id, 4)}
                {renderPickBox(scenario.id, 7)}
                {renderPickBox(scenario.id, 8)}
                {renderPickBox(scenario.id, 10)}
                {renderPickBox(scenario.id, 11)}
                {renderPickBox(scenario.id, 14)}
                {renderPickBox(scenario.id, 15)}
                {renderPickBox(scenario.id, 18)}
                {renderPickBox(scenario.id, 19)}
                {renderPickBox(scenario.id, 22)}
                {renderPickBox(scenario.id, 23)}
        </TeamColumn>
        
        <TeamColumn $team="Dire">
          {/* Dire column */}
                {renderPickBox(scenario.id, 2)}
                {renderPickBox(scenario.id, 3)}
                {renderPickBox(scenario.id, 5)}
                {renderPickBox(scenario.id, 6)}
                {renderPickBox(scenario.id, 9)}
                {renderPickBox(scenario.id, 12)}
                {renderPickBox(scenario.id, 13)}
                {renderPickBox(scenario.id, 16)}
                {renderPickBox(scenario.id, 17)}
                {renderPickBox(scenario.id, 20)}
                {renderPickBox(scenario.id, 21)}
                {renderPickBox(scenario.id, 24)}
        </TeamColumn>
      </PicksContainer>
          </DraftBoardContainer>
        ))}
      </ScenarioBoardsRow>
      
      {/* Hero selection popup as centered overlay */}
      {showPopup && selectedBox !== -1 && (
        <>
          {/* Semi-transparent backdrop */}
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 999
            }}
            onClick={() => setShowPopup(false)}
          />
          
          <HeroSelectPopup ref={popupRef}>
            <div style={{ 
              padding: '8px 16px',
              borderBottom: '1px solid #444',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ fontWeight: 'bold', color: '#eee' }}>
                Select Hero for Position {selectedBox}
              </div>
              <div 
                style={{ 
                  cursor: 'pointer', 
                  fontSize: '20px',
                  color: '#888',
                  padding: '0 8px'
                }}
                onClick={() => setShowPopup(false)}
              >
                ×
              </div>
            </div>
            
            <div style={{ position: 'relative' }}>
              <SearchInput
                type="text"
                placeholder="Search heroes... (Enter: select first, ESC: close)"
                value={heroSearch}
                onChange={(e) => setHeroSearch(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                autoFocus
              />
              <SearchIcon>🔍</SearchIcon>
            </div>
            
            <HeroGrid>
              {filteredHeroes.map(hero => (
                <HeroOption 
                  key={hero.id}
                  onClick={() => handleHeroSelect(hero)}
                >
                  <HeroImage hero={hero} size="small" />
                  <div className="hero-name">{hero.name}</div>
                </HeroOption>
              ))}
              {filteredHeroes.length === 0 && (
                <div style={{ gridColumn: 'span 4', textAlign: 'center', padding: '20px', color: '#888' }}>
                  No heroes match your search
                </div>
              )}
            </HeroGrid>
            
            {activeScenario && activeScenario.picks && activeScenario.picks[selectedBox - 1]?.hero && (
              <PopupControls>
                <RemoveButton onClick={removeHero}>
                  Remove Hero
                </RemoveButton>
              </PopupControls>
            )}
          </HeroSelectPopup>
        </>
      )}

      {/* Drag and drop overlay */}
      <DragOverlay $show={isDragOver}>
        <DragOverlayContent>
          <DragOverlayIcon>📄</DragOverlayIcon>
          <div>Drop to import draft</div>
        </DragOverlayContent>
      </DragOverlay>

      {/* Confirmation dialog for unsaved changes */}
      <ConfirmationDialog
        isOpen={showConfirmation}
        title="Unsaved Changes"
        message="You have unsaved changes. Would you like to save them before continuing?"
        onSave={handleConfirmSave}
        onDiscard={handleConfirmDiscard}
        onCancel={handleConfirmCancel}
        saveButtonText="Save & Continue"
        discardButtonText="Discard Changes"
        cancelButtonText="Cancel"
      />
    </MainContainer>
  );
};

// Add a simple Button component
const Button = styled.button`
  cursor: pointer;
  font-weight: bold;
  transition: all 0.2s;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.4);
  }
  
  &:active {
    transform: translateY(1px);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
  }
`;

export default DraftBoard; 